import { useState } from 'react';
import { Cable, Play, RefreshCw } from 'lucide-react';
import { api, unwrap, unwrapPaged } from '@/lib/api';
import { useLocale } from '@/i18n/locale-provider';
import { useAsync } from '@/lib/use-async';
import { useAuth } from '@/lib/auth';
import {
  Badge, Card, EmptyState, ErrorPanel, PageHeader, Spinner, Table, Td, statusTone,
} from '@/components/ui';

interface Connector {
  id: string;
  sourceId: string;
  name: string;
  description: string | null;
  type: string;
  status: string;
  supportedEntities: string[];
  lastTestedAt: string | null;
  lastTestError: string | null;
  lastSyncedAt: string | null;
  isActive: boolean;
}

interface Template {
  code: string;
  entity: string;
  description: string;
  descriptionAr: string | null;
  fields: string[];
  containsPersonalData: boolean;
}

interface RetrievalLog {
  id: string;
  sourceId: string;
  entity: string;
  queryTemplate: string;
  recordCount: number;
  outcome: string;
  durationMs: number | null;
  errorMessage: string | null;
  requestedByType: string | null;
  createdAt: string;
}

export function ConnectorsPage() {
  const { t, n, dateTime, fill, field } = useLocale();
  const { can } = useAuth();
  const writable = can('connectors:write');
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);

  const connectors = useAsync(() => unwrap<Connector[]>(api.connector.get('/connectors')));
  const templates = useAsync(() => unwrap<Template[]>(api.connector.get('/retrieval/templates')));
  const logs = useAsync(() => unwrapPaged<RetrievalLog>(api.connector.get('/retrieval/logs?pageSize=20')));

  async function act(id: string, action: 'test' | 'sync') {
    setBusy(id + action);
    setActionError(null);
    try {
      await api.connector.post(`/connectors/${id}/${action}`);
      connectors.reload();
    } catch (err) {
      setActionError(err);
    } finally {
      setBusy(null);
    }
  }

  if (connectors.loading && !connectors.data) return <Spinner />;
  if (connectors.error) {
    return (
      <>
        <PageHeader title={t.connectors.title} />
        <ErrorPanel error={connectors.error} onRetry={connectors.reload}
          title={t.connectors.unreachable} />
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={t.connectors.title}
        subtitle={t.connectors.subtitle}
      />

      {actionError && <div className="mb-5"><ErrorPanel error={actionError} title={t.connectors.actionFailed} /></div>}

      <div className="grid gap-4 lg:grid-cols-2">
        {connectors.data?.length ? (
          connectors.data.map((connector) => (
            <Card key={connector.id}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="text-base font-semibold">{connector.name}</h3>
                  <p className="ltr-embed mt-0.5 font-mono text-xs text-deep-600">{connector.sourceId}</p>
                  {connector.description && (
                    <p className="mt-1.5 text-sm text-mist-600">{connector.description}</p>
                  )}
                </div>
                <Badge tone={statusTone(connector.status)}>{connector.status}</Badge>
              </div>

              <div className="mt-3 flex flex-wrap gap-1">
                {connector.supportedEntities.map((entity) => (
                  <span key={entity} className="rounded bg-mist-100 px-1.5 py-0.5 text-xs text-mist-600">
                    {entity}
                  </span>
                ))}
              </div>

              <dl className="mt-4 grid grid-cols-2 gap-3 border-t border-mist-100 pt-3 text-xs">
                <div>
                  <dt className="text-mist-500">{t.connectors.lastTested}</dt>
                  <dd className="mt-0.5 text-mist-700">{dateTime(connector.lastTestedAt)}</dd>
                </div>
                <div>
                  <dt className="text-mist-500">{t.connectors.lastSynced}</dt>
                  <dd className="mt-0.5 text-mist-700">{dateTime(connector.lastSyncedAt)}</dd>
                </div>
              </dl>

              {connector.lastTestError && (
                <p className="mt-3 rounded-md bg-danger-bg px-3 py-2 text-xs text-danger-fg">
                  {connector.lastTestError}
                </p>
              )}

              {writable && (
                <div className="mt-4 flex gap-2">
                  <button
                    type="button"
                    onClick={() => act(connector.id, 'test')}
                    disabled={busy !== null}
                    className="btn-secondary !py-1.5 !text-xs"
                  >
                    <Play size={12} aria-hidden />
                    {busy === connector.id + 'test' ? t.connectors.testing : t.connectors.testConnection}
                  </button>
                  <button
                    type="button"
                    onClick={() => act(connector.id, 'sync')}
                    disabled={busy !== null}
                    className="btn-secondary !py-1.5 !text-xs"
                  >
                    <RefreshCw size={12} aria-hidden />
                    {busy === connector.id + 'sync' ? t.connectors.syncing : t.connectors.syncSchema}
                  </button>
                </div>
              )}
            </Card>
          ))
        ) : (
          <div className="lg:col-span-2">
            <EmptyState icon={Cable} title={t.connectors.noneTitle} detail={t.connectors.noneBody} />
          </div>
        )}
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <Card title={fill(t.connectors.allowlistTitle, { count: n(templates.data?.length ?? 0) })}>
          <p className="mb-3 text-xs text-mist-500">
            {t.connectors.allowlistNote}
          </p>
          <div className="max-h-96 space-y-1.5 overflow-y-auto">
            {templates.data?.map((template) => (
              <div key={template.code} className="rounded-md border border-mist-200 px-3 py-2">
                <div className="flex items-center justify-between gap-2">
                  <code className="text-xs font-semibold text-deep-600">{template.code}</code>
                  {template.containsPersonalData && <Badge tone="warn">{t.connectors.personalFiltered}</Badge>}
                </div>
                <p className="mt-1 text-xs text-mist-600">{field(template, 'description')}</p>
              </div>
            ))}
          </div>
        </Card>

        <Card title={t.connectors.auditTitle} action={
          <button type="button" onClick={logs.reload} className="btn-ghost !py-1 !text-xs">{t.common.refresh}</button>
        }>
          <Table head={[t.connectors.query, t.connectors.recordsCol, t.connectors.outcome, t.connectors.when]} empty={!logs.data?.items.length}>
            {logs.data?.items.map((log) => (
              <tr key={log.id} className="hover:bg-mist-50">
                <Td>
                  <code className="text-xs text-deep-600">{log.queryTemplate}</code>
                  <p className="ltr-embed mt-0.5 text-[10px] text-mist-400">
                    {log.sourceId}
                    {log.requestedByType && ` · ${log.requestedByType}`}
                  </p>
                </Td>
                <Td className="tabular text-end text-mist-700">{n(log.recordCount)}</Td>
                <Td>
                  <Badge tone={log.outcome === 'SUCCESS' ? 'good' : log.outcome === 'DENIED' ? 'warn' : 'bad'}>
                    {log.outcome}
                  </Badge>
                  {log.durationMs !== null && (
                    <span className="tabular ms-1.5 text-[10px] text-mist-400">{n(log.durationMs)}ms</span>
                  )}
                </Td>
                <Td className="text-xs text-mist-500">{dateTime(log.createdAt)}</Td>
              </tr>
            ))}
          </Table>
        </Card>
      </div>
    </>
  );
}
